import React, { useState, useEffect, useContext, useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoice, faCheckCircle, faSave, faPaperPlane, faUserShield, faFolderOpen, faFilePdf, faExclamationTriangle, faFileUpload, faPaperclip, faHistory, faRedo, faColumns } from '@fortawesome/free-solid-svg-icons';
import { SUPPLIER_DOCUMENT_ACCEPT, supplierDocumentProblem } from '../../components/Invoicing/supplierDocumentFile';
import { getErrorMessage } from '../../utils/errorMessages';
import Layout from '../../components/Layout';

import Footer from '../../components/ui/Footer';
import IncomingInvoiceScreen1 from './IncomingInvoiceScreen1';
import IncomingInvoiceScreen2 from './IncomingInvoiceScreen2';
import IncomingInvoiceScreen3 from './IncomingInvoiceScreen3';
import invoicingService from '../../services/invoicingService';
import { useFlashMessage } from '../../components/FlashMessage';
import InvoiceValidationModal from '../../components/Invoicing/InvoiceValidationModal';
import FinancialApprovalModal from '../../components/Invoicing/FinancialApprovalModal';
import { bdnApprovalWarnings } from '../../utils/bdnQuantityNote';
import { AuthContext } from '../../context/AuthContext';
import InvoiceDocumentPanel from '../../components/Invoicing/InvoiceDocumentPanel';
import SupplierPdfChooserModal from '../../components/shared/SupplierPdfChooserModal';
import InvoiceAuditModal from '../../components/Invoicing/InvoiceAuditModal';
import InvoiceCaptureModal from '../../components/Invoicing/InvoiceCaptureModal';
import DeliveryLinkBadge from '../../components/Invoicing/DeliveryLinkBadge';
import AbacusStatus from '../../components/Abacus/AbacusStatus';
import HMSPageHeader from '../../components/ui/HMSPageHeader';
import HMSStepper from '../../components/ui/HMSStepper';
import DocumentsSplitPane from '../../components/DocumentsSplitPane';
import RelatedDocumentsPane, { useInvoicePapersOpen } from '../../components/RelatedDocumentsPane';
import { PRICE_DECIMALS, formatFigure } from '../../utils/precision';
import useDocumentReturn from '../../components/shared/useDocumentReturn';
import { useSendToFinance, SendToFinanceNote } from '../../components/Invoicing/useSendToFinance';
import CrmMatchFlags from '../../components/Invoicing/CrmMatchFlags';
import RecordLoadProblem, { describeLoadFailure } from '../../components/ui/RecordLoadProblem';
import './OutgoingInvoice.css';
import { extractApiError } from '../../utils/errorMessages';
import PinToDeskButton from '../../components/mydesk/PinToDeskButton';
import { withEnglishUnderneath } from '../../components/Invoicing/utils/captureText';

/**
 * Create/Edit Incoming Invoice - 3-Screen Flow Orchestrator (Accounts Payable)
 * 
 * Manages:
 * - State for all 3 screens
 * - Navigation between screens
 * - Save draft functionality
 * - Submit for approval workflow
 * - Creation from Purchase Order (future)
 * - Editing existing invoices
 * 
 * Key Differences from Outgoing:
 * - Supplier invoice (not customer)
 * - HMS expense tracking number
 * - Payment scheduling
 * - Supplier's invoice number captured
 * - No PDF generation (we receive supplier's PDF)
 */

/**
 * Turn what the capture tool read out of a supplier PDF into invoice HEADER
 * fields.
 *
 * Two doors now reach this screen with captured data — the Capture button on the
 * Incoming Invoices list (which navigates here carrying route state) and the
 * Capture button on this screen itself. They must fill the form identically, so
 * the mapping lives here once instead of once per door. A control that behaves
 * differently depending on how you arrived is the fault this whole change
 * exists to remove.
 *
 * **This returns a PATCH, never a replacement.** The delivery link
 * (`source_delivery`, `_prefilled_from_delivery`, `_prefilled_delivery_number`)
 * is deliberately absent from the returned object, so merging it over the
 * current form leaves the delivery badge exactly where it was. That is AC3b:
 * a trader arriving from a delivery may read a PDF in AND keep the link —
 * before this, capture was only reachable from a door that had never heard of
 * deliveries, so they had to choose.
 *
 * @param {object} captured   - the selected fields from InvoiceCaptureModal
 * @param {object} prev       - the current header state, used for fallbacks
 * @param {string|null} analysisId - the capture the server should attach on save
 */
const buildCaptureHeaderPatch = (captured, prev, analysisId) => ({
  supplier_invoice_number: captured.supplier_invoice_number || prev.supplier_invoice_number,
  invoice_date: captured.invoice_date || prev.invoice_date,
  due_date: captured.due_date || prev.due_date,
  currency: captured.currency || prev.currency,
  supply_reference: captured.supply_reference || prev.supply_reference,
  notes: captured.notes ? withEnglishUnderneath(captured.notes, captured.notes_en) : prev.notes,
  net_amount: captured.net_amount || prev.net_amount,
  vat_amount: captured.vat_amount || prev.vat_amount,
  gross_amount: captured.gross_amount || prev.gross_amount,
  _capture_supplier_name: captured.supplier_name || null,
  _capture_supplier_iban: captured.supplier_iban || null,
  _capture_payment_terms: captured.payment_terms_description || null,
  _capture_buyer_entity_name: captured.buyer_entity_name || null,
  _capture_analysis_id: analysisId,
});

/**
 * The blank line a captured item lands on when the form had no line there.
 *
 * Only used to fill the gaps — never to overwrite a line the delivery already
 * supplied. See `mergeCaptureLineItems`.
 */
const EMPTY_LINE = {
  item_description: '',
  quantity: '',
  unit: null,
  unit_price: '',
  net_amount: '',
  vat_rate: null,
  vat_amount: '',
  gross_amount: '',
  gl_account: null,
  product: null,
};

/* A value the PDF actually gave us, as opposed to "the reader found nothing".
 * `0` counts (a zero-rated line is a real answer); '' , null and undefined do
 * not. Getting this wrong in either direction is the whole defect: too strict
 * and the capture cannot correct an amount, too loose and it erases coding. */
const capturedSomething = (value) =>
  value !== null && value !== undefined && value !== '';

/**
 * The captured line items, as a PATCH per line — the same contract the header
 * has always had.
 *
 * **This must not name a field the PDF said nothing about.** The previous
 * version emitted `unit: null`, `vat_rate: null`, `gl_account: null` and no
 * `product` at all, and the caller then REPLACED the line array with it. So an
 * operator who opened a delivery, got a line pre-filled with its product, unit
 * MT, GL account and VAT rate, and then read the supplier's PDF in, watched all
 * four of those disappear — a supplier invoice with no finance coding on it,
 * produced by a button whose whole purpose was to save typing. A supplier PDF
 * does not know our chart of accounts; it can never be the reason a GL account
 * goes blank.
 *
 * The `_capture_*` keys are different in kind: they describe THIS capture (the
 * unit code the PDF printed, the product the matcher guessed), so they are
 * always written, including when they are null — a stale suggestion left over
 * from a previous capture would be worse than none.
 */
const buildCaptureLineItems = (captured) => (captured.line_items || []).map((item, idx) => {
  const qty = item.quantity || 1;
  let unitPrice = item.unit_price || '';
  const netAmt = item.net_amount || '';

  if ((!item.quantity || !item.unit_price) && netAmt) {
    const parsedQty = parseFloat(qty) || 1;
    const parsedNet = parseFloat(netAmt) || 0;
    if (parsedQty > 0 && parsedNet > 0 && !item.unit_price) {
      // A derived price keeps up to PRICE_DECIMALS (at least 2), not cents.
      unitPrice = formatFigure(parsedNet / parsedQty, 2, PRICE_DECIMALS);
    }
  }

  return {
    line_number: idx + 1,
    item_description: withEnglishUnderneath(item.item_description, item.item_description_en),
    quantity: qty,
    unit_price: unitPrice,
    net_amount: netAmt,
    // Sprint A: synthetic skip-lines line carries the captured header
    // VAT amount; preserve it instead of blanking so the form's
    // calculateTotals() guard sees a non-zero VAT for header reproduction.
    vat_amount: item._capture_skip_lines ? (item.vat_amount || '') : '',
    gross_amount: item.gross_amount || '',
    currency: captured.currency || 'USD',
    _capture_unit_code: item.unit || null,
    _capture_vat_rate_percentage: item.vat_rate_percentage || null,
    _capture_skip_lines: !!item._capture_skip_lines,
    // Sprint C: auto-match annotations from InvoiceCaptureModal.
    // _capture_product_id is the variant id when the description
    // exact-matched a stock variant; the form-side resolver in
    // IncomingInvoiceScreen2.loadReferenceData hydrates `product`
    // and seeds the search term with _capture_product_display_name.
    // _capture_product_suggestion is the top-1 fuzzy hit when no
    // exact match was found; rendered as a "Suggested: ..." chip
    // with Link / Dismiss buttons in the description cell.
    _capture_product_id: item._capture_product_id || null,
    _capture_product_display_name: item._capture_product_display_name || null,
    _capture_product_suggestion: item._capture_product_suggestion || null,
  };
});

/**
 * Lay the captured lines OVER the lines already on the form.
 *
 * Rules, in one sentence each:
 *   - the PDF decides how many lines there are (it is the document being
 *     invoiced, so a line it does not contain is not on this invoice);
 *   - a captured value fills a blank or corrects an amount;
 *   - a field the PDF said nothing about keeps whatever the form already had —
 *     product, unit, GL account, VAT rate, cost coding, all of it;
 *   - the `_capture_*` annotations always come from this capture.
 *
 * Lines are matched by POSITION. A supplier invoice raised from a delivery is
 * the same goods in the same order — one bunker stem, one line — and there is
 * no shared key between a PDF row and a delivery row to match on instead.
 * Where the PDF has more lines than the delivery did, the extras start from
 * `EMPTY_LINE` and are coded by hand, exactly as a typed invoice would be.
 */
const mergeCaptureLineItems = (previousLines, capturedLines) =>
  capturedLines.map((capturedLine, idx) => {
    const merged = { ...EMPTY_LINE, ...(previousLines[idx] || {}) };
    Object.entries(capturedLine).forEach(([field, value]) => {
      if (field.startsWith('_capture_') || field === 'line_number') {
        merged[field] = value;
        return;
      }
      if (capturedSomething(value)) merged[field] = value;
    });
    return merged;
  });

const CreateIncomingInvoice = () => {
  const history = useHistory();
  const { showSuccess, showError } = useFlashMessage();
  const location = useLocation();
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  // The Documents and Preview Supplier PDF buttons used to be HIDDEN from a
  // trade-ops operator, because the document doors behind them were
  // finance-only and would have refused them. Hiding was the wrong fix: it left
  // an operator able to raise a supplier invoice and unable ever to attach its
  // paper. Marcel, 2026-09-30: "Gabriel en Fitri kan nie". The doors now admit an
  // operator for a supplier invoice on their own delivery
  // (invoicing.delivery_scoped_access.HasFinancialAccessOrOwnInvoiceDocuments),
  // and this screen only ever opens such an invoice for them, so both buttons
  // show for everyone.
  
  // Check if creating from purchase order (future feature)
  const sourcePO = location.state?.purchase_order || null;
  
  // Check if creating from invoice capture (OCR extraction)
  const capturePreFilledData = location.state?.preFilledData || null;
  const captureAnalysisId = location.state?.analysisId || null;
  const captureAttachedOnly = Boolean(location.state?.attachedOnly);
  // STATE, not a derived constant: an in-place capture on this screen produces a
  // fresh CRM match that Screen 1 must see. When we arrived from the list door
  // the route state seeds it, exactly as before.
  const [capturePartner, setCapturePartner] = useState(location.state?.crmPartner || null);
  
  // State management
  const [currentScreen, setCurrentScreen] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(!!id);
  // Row 125: why the supplier invoice in the address didn't load. While set,
  // the screen shows RecordLoadProblem INSTEAD of the editor, and the id that
  // did load is what lets a save through -- a blank editor must never be saved
  // over the real invoice.
  const [loadProblem, setLoadProblem] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [loadedId, setLoadedId] = useState(null);
  // Derived, never stored. As state it was captured once at mount and never
  // updated, so any route change between /incoming/create and /incoming/:id
  // left it stale -- the header kept saying CREATE, and worse, the save handler
  // reads it to choose between update and create, so an edit made after such a
  // navigation would have POSTed a NEW invoice instead of updating this one.
  // Surfaced by the duplicate banner's "Open it" link, which is the first thing
  // in HMS to route between those two URLs without a reload.
  // The id of the document this screen is editing -- from the URL when we
  // arrived on one, or adopted from the server the moment a draft is first
  // created. Held here so a second Save Draft UPDATES the document instead of
  // POSTing a duplicate.
  //
  // isEditMode used to be useState(!!id), and setIsEditMode was never called
  // anywhere in five of the six creators, so the flag was frozen at mount. The
  // old redirect-to-the-list hid that: you left the screen before you could
  // press Save Draft twice. Now that saving keeps you here, the flag has to
  // follow the document.
  const [createdId, setCreatedId] = useState(null);
  const documentId = id || createdId;
  const isEditMode = Boolean(documentId);
  // Invoice Papers: the delivery's papers and contracts BESIDE the invoice, so
  // an approver checks one against the other without leaving the screen
  // (ClickUp 2026-10-23, "double screen ... where there are approvals").
  // Remembered per browser; the Documents overlay stays as it was.
  const invoicePapers = useInvoicePapersOpen();
  // Who RAISED this document. Dropped by this loader until 2026-08-26, which
  // left Screen 1 showing whoever was logged in instead.
  const [documentIssuerName, setDocumentIssuerName] = useState(null);
  
  // Validation modal state
  const [showDraftValidationModal, setShowDraftValidationModal] = useState(false);
  const [draftValidationErrors, setDraftValidationErrors] = useState([]);
  const [targetScreenForValidation, setTargetScreenForValidation] = useState(null);
  
  // Approval modal state
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [invoiceStatus, setInvoiceStatus] = useState(null);

  // Capture modal state. **2026-09-07, Marcel:** "we choose capture invoice and
  // it opens an incoming screen but no capture button". The upload-and-read tool
  // was mounted only on the LIST screen, which has never heard of deliveries —
  // so an operator arriving here from a delivery got a blank typing form. The
  // control belongs to the incoming-invoice screen, not to the door you came
  // through.
  const [isCaptureModalOpen, setIsCaptureModalOpen] = useState(false);
  // Attach document: the supplier's file, uploaded WITHOUT the AI reading it
  // (Marcel, 2026-09-30: "ons capture nie"). The name is only for the screen;
  // the document itself travels as _capture_analysis_id like a capture does.
  const [attachedFileName, setAttachedFileName] = useState(null);
  const [isAttaching, setIsAttaching] = useState(false);
  const attachInputRef = useRef(null);

  // Document panel state
  const [isDocumentPanelOpen, setIsDocumentPanelOpen] = useState(false);
  const [documentCount, setDocumentCount] = useState(0);
  const [showSupplierPdfModal, setShowSupplierPdfModal] = useState(false);
  // The PDF this invoice was captured from: kept with it, and whether it has
  // reached the archive (Gritt 2026-10-19: no PDF to view, no audit log).
  const [capturedDocument, setCapturedDocument] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [retryingFiling, setRetryingFiling] = useState(false);

  const openCapturedPdf = async () => {
    try {
      const blob = await invoicingService.fetchIncomingCaptureFile(documentId);
      const url = URL.createObjectURL(blob);
      // Only a PDF or an image comes back to be shown (the server decides);
      // anything else arrives as a download. A blob URL drops the server's
      // filename, so opening it saved the file under a random id.
      if (blob.type === 'application/octet-stream') {
        const link = document.createElement('a');
        link.href = url;
        link.download = capturedDocument?.filename || 'supplier-document';
        link.click();
      } else {
        window.open(url, '_blank', 'noopener');
      }
    } catch (err) {
      showError(getErrorMessage(err, 'The captured PDF could not be opened.'));
    }
  };

  const retryFiling = async () => {
    setRetryingFiling(true);
    try {
      const result = await invoicingService.retryIncomingCaptureFiling(documentId);
      setCapturedDocument(prev => ({
        ...(prev || {}), in_archive: Boolean(result.filed), filing_problem: result.filing_problem,
      }));
      if (result.filed) showSuccess('The PDF is now in the archive.');
      else showError(result.filing_problem || 'Filing into the archive failed again.');
    } catch (err) {
      showError(getErrorMessage(err, 'Filing into the archive failed. Try again.'));
    } finally {
      setRetryingFiling(false);
    }
  };
  
  // Determine if invoice is locked (read-only) based on status
  const isReadOnly = invoiceStatus && !['DRAFT', 'PENDING_APPROVAL', 'REJECTED'].includes(invoiceStatus);
  
  // Load existing invoice if editing
  useEffect(() => {
    const loadInvoice = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        setLoadProblem(null);
        const data = await invoicingService.fetchIncomingInvoice(id);
        setDocumentIssuerName(data.issuer_name || null);
        
        // Track invoice status for approval workflow
        setInvoiceStatus(data.status);
        
        // Populate invoice data
        setInvoiceData({
          invoice_number: data.invoice_number || '',
          hms_entity: data.hms_entity,
          invoice_date: data.invoice_date,
          due_date: data.due_date,
          due_date_basis: data.due_date_basis || 'INVOICE_DATE',
          partner: data.partner,
          partner_name: data.partner_name,
          invoice_address: data.invoice_address || {},
          payment_terms: data.payment_terms,
          supplier_invoice_number: data.supplier_invoice_number || '',
          internal_reference: data.internal_reference || '',
          supply_reference: data.supply_reference || '',
          currency: data.currency,
          number_format: data.number_format || 'US',
          supplier_bank_account: data.supplier_bank_account,
          cost_center: data.cost_center,
          cost_category: data.cost_category,
          reporting_month: data.reporting_month || '',
          notes: data.notes || '',
          notes_visible_on_invoice: data.notes_visible_on_invoice || false,
          source_purchase_order: data.source_purchase_order || null,
          source_delivery: data.source_delivery || null,
          source_delivery_number: data.source_delivery_number || null,
          source_third_party_cost: data.source_third_party_cost || null,
          net_amount: data.net_amount,
          vat_amount: data.vat_amount,
          gross_amount: data.gross_amount,
          // Seed the CRM cross-check inputs from the stored OCR capture so a
          // saved invoice (e.g. opened by the approver) is checked against the
          // same PDF-derived name/IBAN as at creation time.
          _capture_supplier_name: data.ocr_extracted_data?.supplier_name || null,
          _capture_supplier_iban: data.ocr_extracted_data?.supplier_iban || null,
        });
        setCapturedDocument(data.captured_document || null);
        
        // Populate line items (restore CUSTOM vat_rate UI state from API data)
        if (data.line_items) {
          const restored = data.line_items.map(item => {
            if (!item.vat_rate && item.custom_vat_percentage != null) {
              return { ...item, vat_rate: 'CUSTOM' };
            }
            return item;
          });
          setLineItems(restored);
        }
        
        // Populate vessel info
        if (data.vessel_info) {
          setVesselInfo({
            vessel_name: data.vessel_info.vessel_name || '',
            imo_number: data.vessel_info.imo_number || '',
            delivery_date: data.vessel_info.delivery_date || '',
            bl_number: data.vessel_info.bl_number || '',
            bl_date: data.vessel_info.bl_date || '',
            loading_port: data.vessel_info.loading_port || '',
            discharge_port: data.vessel_info.discharge_port || '',
            incoterms: data.vessel_info.incoterms || ''
          });
        }
        
        setLoadedId(id);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading invoice:', error);
        // Keep the answer and say it on the screen, instead of a toast and a
        // bounce to the list that never said whether it was missing or refused.
        setLoadProblem(describeLoadFailure(error));
        setIsLoading(false);
      }
    };
    
    loadInvoice();
  }, [id, loadAttempt]);
  
  // Load delivery data for pre-fill if deliveryId in URL params
  useEffect(() => {
    const loadDeliveryPrefill = async () => {
      const urlParams = new URLSearchParams(location.search);
      const deliveryId = urlParams.get('deliveryId');
      // The supplier (and its recaps) chosen on the delivery's invoice window,
      // or the one third-party cost being billed. Dropping these used to hand
      // every supplier invoice to the recap header supplier.
      const partnerId = urlParams.get('partnerId');
      const recapIdsParam = urlParams.get('recapIds');
      const recapIds = recapIdsParam ? recapIdsParam.split(',').map((rid) => parseInt(rid, 10)) : null;
      const thirdPartyCostId = urlParams.get('thirdPartyCostId');
      
      // Only prefill for new invoices (not editing existing)
      if (deliveryId && !id && !sourcePO) {
        try {
          setIsLoading(true);
          console.log(`Pre-filling incoming invoice from delivery ${deliveryId}`);
          
          const deliveryData = await invoicingService.fetchDeliveryForIncomingInvoice(
            deliveryId, partnerId, recapIds, thirdPartyCostId
          );
          
          // Set invoice header data
          setInvoiceData(prev => ({
            ...prev,
            ...deliveryData.invoice_data,
            _prefilled_from_delivery: deliveryId,
            _prefilled_delivery_number: deliveryData.delivery_display_number
          }));
          
          // Set line items from delivery
          if (deliveryData.line_items_data?.length > 0) {
            setLineItems(deliveryData.line_items_data);
          }
          
          // Set vessel info from delivery
          if (deliveryData.vessel_info_data) {
            setVesselInfo(deliveryData.vessel_info_data);
          }
          
          // Check if any required fields are missing
          const missingFields = [];
          if (!deliveryData.invoice_data.hms_entity) missingFields.push('Entity');
          if (!deliveryData.invoice_data.partner) missingFields.push('Supplier');
          if (!deliveryData.invoice_data.supplier_invoice_number) missingFields.push('Supplier Invoice Number');
          
          const deliveryNumber = deliveryData.delivery_display_number || `#${deliveryId}`;

          // A figure the deal does not state arrives as 0 on its line. Say
          // which, so a zero is never mistaken for an agreed price.
          if (deliveryData.missing_figures?.length > 0) {
            showError(
              `The deal does not state the ${deliveryData.missing_figures.join(', ')}. ` +
              'Those lines show 0 - enter the figure from the supplier\'s invoice.',
              12000
            );
          }
          
          if (missingFields.length > 0) {
            showSuccess(
              `Data pre-filled from Delivery ${deliveryNumber}. ` +
              `Please provide: ${missingFields.join(', ')}`,
              8000
            );
          } else {
            showSuccess(`Incoming invoice data pre-filled from Delivery ${deliveryNumber}`, 8000);
          }
          console.log('Incoming invoice pre-fill complete:', deliveryData);
        } catch (error) {
          console.error('Error loading delivery for incoming invoice pre-fill:', error);
          const errorMessage = error.response?.data?.error || 'Failed to load delivery data. Please fill manually.';
          showError(errorMessage, 8000);
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    loadDeliveryPrefill();
  }, [location.search, id, sourcePO]);
  
  /**
   * Put what the capture tool read into this form — WITHOUT disturbing anything
   * the form already knows.
   *
   * The merge is a patch over `prev`, and that is the whole point: the delivery
   * link pre-filled from `?deliveryId=` (`source_delivery` and the two
   * `_prefilled_*` fields the green D-number badge reads) is never named in the
   * patch, so it survives a capture untouched. Before the Capture button existed
   * on this screen a trader had to pick one or the other — arrive from the
   * delivery and type the invoice by hand, or capture the PDF from the list and
   * lose the delivery. They can now have both, and the invoice still saves
   * against the delivery it was started from.
   */
  const applyCapturedInvoice = (captured, analysisId, crmPartner = null, attachedOnly = false) => {
    // A capture replaces any document attached by hand, so stop naming that one.
    setAttachedFileName(null);
    setInvoiceData(prev => ({
      ...prev,
      ...buildCaptureHeaderPatch(captured, prev, analysisId),
    }));

    const capturedLines = buildCaptureLineItems(captured);
    // Only touch the lines when the PDF actually yielded some. An extraction
    // that found no line items must not wipe lines already pre-filled from the
    // delivery.
    //
    // And when it did yield some, they are MERGED, not substituted. The header
    // above has always been a patch; the lines used to be a replacement, which
    // meant reading a supplier PDF silently stripped the product, unit, GL
    // account and VAT rate the delivery had supplied. Same rule for both halves
    // of the form now: a capture may fill a blank and correct an amount, and
    // may never delete coding it knows nothing about.
    if (capturedLines.length > 0) {
      setLineItems(prevLines => mergeCaptureLineItems(prevLines || [], capturedLines));
    }

    if (crmPartner) setCapturePartner(crmPartner);

    // Attached without reading: nothing on the form changed, so say what DID
    // happen rather than claim data was extracted.
    showSuccess(
      attachedOnly
        ? "Supplier's document attached. It couldn't be read automatically, so fill in the details yourself. It will be filed with the invoice when you save."
        : 'Invoice data extracted from uploaded document. Please review and complete the remaining fields.',
      8000
    );
  };

  const handleAttachFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    // Cleared at once so the same file can be chosen again after a refusal.
    event.target.value = '';
    if (!file) return;
    const problem = supplierDocumentProblem(file);
    if (problem) {
      showError(problem);
      return;
    }
    setIsAttaching(true);
    try {
      const result = await invoicingService.attachSupplierDocument(file);
      setInvoiceData(prev => ({ ...prev, _capture_analysis_id: result.analysis_id }));
      setAttachedFileName(result.filename || file.name);
      showSuccess(
        `Supplier's document attached: ${result.filename || file.name}. It will be filed with the invoice when you save.`,
        6000
      );
    } catch (error) {
      showError(getErrorMessage(error, "The supplier's document could not be attached. Please try again."));
    } finally {
      setIsAttaching(false);
    }
  };

  // Pre-fill from invoice capture (OCR extraction) handed over as ROUTE STATE by
  // the Incoming Invoices list. The in-place Capture button below runs the same
  // mapping through `applyCapturedInvoice`; this effect only exists because that
  // door hands the data over across a navigation.
  useEffect(() => {
    if (capturePreFilledData && !id) {
      console.log('Pre-filling from invoice capture:', capturePreFilledData);
      applyCapturedInvoice(capturePreFilledData, captureAnalysisId, null, captureAttachedOnly);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Scroll to top on mount and screen change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentScreen]);
  
  // Invoice header data (Screen 1)
  const [invoiceData, setInvoiceData] = useState({
    hms_entity: null,
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: null,
    due_date_basis: 'INVOICE_DATE',
    partner: null, // Supplier
    partner_name: '',
    invoice_address: {},
    payment_terms: null,
    supplier_invoice_number: '', // Supplier's original invoice number
    internal_reference: '',
    supply_reference: '',
    currency: 'USD',
    number_format: 'US',
    supplier_bank_account: null,
    cost_center: null,
    cost_category: null,
    reporting_month: new Date().toISOString().substring(0, 8) + '01',
    notes: '',
    notes_visible_on_invoice: false,
    source_purchase_order: sourcePO?.id || null
  });

  // A supplier invoice is never saved without its document (Marcel,
  // 2026-09-30). The document arrives WITH a new invoice as a capture, so until
  // one has been applied a NEW invoice cannot be saved at all. The server refuses
  // exactly this (invoicing.document_required); the screen says so first instead
  // of letting someone fill three screens and meet a refusal at the end. Once
  // the invoice exists, its document travels in the Documents panel instead.
  const needsDocumentFirst = !documentId && !invoiceData._capture_analysis_id;
  const NEEDS_DOCUMENT_MESSAGE = "Attach the supplier's invoice first: press Attach document and choose their file (PDF, Word, Excel or a photo or scan), or Capture Invoice to have it read in. A supplier invoice can't be saved without it, because the approver needs something to check it against.";

  // Where this screen hands the user back to. **2026-09-09, HMS:** *"could this
  // stay in the delivery pls"*. The exits below used to hard-code '/invoicing/incoming';
  // an operator who arrived from their delivery was dropped into the Finance
  // invoice table instead of back on the job they were doing. One shared rule
  // now decides for all six invoice screens: you return to whichever screen you
  // actually came in from. See components/shared/useDocumentReturn.js.
  const { returnLabel, goBack, goBackAfterSave } = useDocumentReturn({
    listPath: '/invoicing/incoming',
    listLabel: 'Incoming Invoices',
    user,
    sourceDeliveryId: invoiceData.source_delivery || invoiceData._prefilled_from_delivery || null,
    sourceDeliveryNumber: invoiceData.source_delivery_number || invoiceData._prefilled_delivery_number || null,
  });

  // Save & Workflow on a bunker delivery's invoice belongs to the Bunker
  // Operations Approvals group (2026-09-17). The server answers who may press
  // it, with the same rule the save enforces.
  const sendToFinance = useSendToFinance({
    document: 'incoming',
    id: documentId || null,
    deliveryId: invoiceData.source_delivery || invoiceData._prefilled_from_delivery || null,
  });

  
  // Line items (Screen 2)
  const [lineItems, setLineItems] = useState([]);

  // CRM cross-check (advisory, never blocks): compare what the invoice says
  // (typed or AI-captured) with what CRM knows about the selected partner.
  // Re-runs (debounced) whenever the compared inputs change. Declared AFTER
  // invoiceData — the dependency array reads it at render time.
  const [crmMatch, setCrmMatch] = useState(null);
  // The invoice that already holds this supplier invoice number, when a save is
  // refused as a duplicate. Kept on the screen rather than in the toast: the
  // toast is gone in eight seconds, and this is the thing the user needs to act
  // on. Shipped by the API as `duplicate_invoice` so nothing has to be parsed
  // out of the message text.
  const [duplicateInvoice, setDuplicateInvoice] = useState(null);

  // A duplicate notice is about ONE number. The moment the user edits it the
  // notice is stale, and a stale "already exists" banner reads as an unfixable
  // block. Declared here, below `invoiceData`: a hook that reads state defined
  // further down the component body throws on first render and takes the whole
  // screen with it.
  // `id` is in here too: after "Open it" the screen is showing the very invoice
  // the notice pointed at, and its number and supplier are by definition
  // unchanged -- so without this the banner would follow the user across and
  // accuse that invoice of duplicating itself.
  useEffect(() => {
    setDuplicateInvoice(null);
  }, [id, invoiceData.supplier_invoice_number, invoiceData.partner]);
  useEffect(() => {
    const partnerId = invoiceData.partner;
    if (!partnerId) {
      setCrmMatch(null);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await invoicingService.checkInvoiceCrmMatch({
          partner_id: partnerId,
          supplier_name: invoiceData._capture_supplier_name || '',
          iban: invoiceData._capture_supplier_iban || '',
          bank_account_id: invoiceData.supplier_bank_account || null,
        });
        setCrmMatch(result);
      } catch (error) {
        // The check itself failing must be visible, not silently green/absent.
        setCrmMatch({
          overall: 'warning',
          checks: [{
            check: 'identity', status: 'warning',
            detail: 'CRM check could not run. Verify partner details manually.',
          }],
        });
      }
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceData.partner, invoiceData.supplier_bank_account,
      invoiceData._capture_supplier_name, invoiceData._capture_supplier_iban]);
  
  // Vessel info (Screen 2, manual input)
  const [vesselInfo, setVesselInfo] = useState({
    vessel_name: '',
    imo_number: '',
    delivery_date: '',
    bl_number: '',
    bl_date: '',
    loading_port: '',
    discharge_port: '',
    incoterms: ''
  });
  
  const prepareLineItemsForApi = (items) => {
    return items.map(item => {
      const cleaned = { ...item };

      // Ensure numeric fields are valid numbers or null (prevents "A valid number is required" errors)
      ['quantity', 'unit_price', 'net_amount', 'vat_amount', 'gross_amount'].forEach(field => {
        if (cleaned[field] === '' || cleaned[field] === undefined) {
          cleaned[field] = null;
        }
      });

      if (cleaned.vat_rate === 'CUSTOM') {
        cleaned.vat_rate = null;
        cleaned.custom_vat_percentage = parseFloat(cleaned.custom_vat_percentage) || 0;
      } else {
        cleaned.custom_vat_percentage = null;
      }

      // Strip any remaining capture hint fields
      delete cleaned._capture_unit_code;
      delete cleaned._capture_vat_rate_percentage;

      return cleaned;
    });
  };

  // Row 125: with an invoice in the address, only an invoice that actually
  // loaded may be saved -- otherwise the update would write the blank editor
  // over it. Both save paths ask this before choosing update or create.
  const refuseUnloadedSave = () => {
    if (id && loadedId !== id) {
      showError("This supplier invoice hasn't loaded, so it can't be saved. Reload the page and try again.", 8000);
      return true;
    }
    return false;
  };

  const handleSaveDraft = async (modifiedData = null) => {
    if (refuseUnloadedSave()) return;
    try {
      setIsSaving(true);
      
      // Use modified data from Screen3 if provided, otherwise use state
      const dataToUse = modifiedData || invoiceData;
      
      // DRAFT VALIDATION: Only IMT Entity and Partner required
      // Everything else is optional for work in progress
      
      console.log('=== DRAFT VALIDATION DEBUG ===');
      console.log('Full dataToUse:', dataToUse);
      console.log('hms_entity value:', dataToUse.hms_entity);
      console.log('hms_entity type:', typeof dataToUse.hms_entity);
      console.log('partner value:', dataToUse.partner);
      console.log('partner type:', typeof dataToUse.partner);
      
      // Values are stored as direct integers, not objects
      const errors = [];
      if (!dataToUse.hms_entity) {
        errors.push('Entity is required');
      }
      if (!dataToUse.partner) {
        errors.push('Supplier (CRM Profile) is required');
      }
      
      if (errors.length > 0) {
        console.log('Validation FAILED. Errors:', errors);
        setDraftValidationErrors(errors);
        setTargetScreenForValidation(1);
        setShowDraftValidationModal(true);
        setIsSaving(false);
        return;
      }
      
      console.log('Draft validation PASSED!');
      
      const payload = {
        ...dataToUse,
        status: 'DRAFT',
        reporting_month: dataToUse.reporting_month 
          ? (dataToUse.reporting_month.length === 7 
              ? dataToUse.reporting_month + '-01'
              : dataToUse.reporting_month)
          : null,
        line_items: prepareLineItemsForApi(lineItems),
        vessel_info: vesselInfo
      };
      
      let response;
      if (documentId) {
        response = await invoicingService.updateIncomingInvoice(documentId, payload);
      } else {
        response = await invoicingService.createIncomingInvoice(payload);
      }
      
      showSuccess(
        response.hms_expense_number 
          ? `Incoming invoice ${isEditMode ? 'updated' : 'saved'} successfully! IMT Expense Number: ${response.hms_expense_number}, Supplier Invoice: ${response.supplier_invoice_number}`
          : `Incoming invoice draft ${isEditMode ? 'updated' : 'saved'} successfully! Supplier Invoice: ${response.supplier_invoice_number}. Submit for approval to generate IMT expense number.`,
        8000
      );
      
      // Saving a draft KEEPS YOU ON THE DOCUMENT. It used to eject to the list
      // 1.5s later, which is what accounting reported as "it jumps to the
      // table" -- you are mid-edit and the screen throws you out. Only
      // Save & Workflow navigates away now, because there the document really
      // has left your hands.
      //
      // The new id is adopted into STATE, deliberately NOT into the URL:
      // /create and /edit/:id are separate <Route>s inside App.js's <Switch>,
      // so replacing the URL would unmount and remount this screen and drop the
      // user back on step 1. A smaller jump than the old one, but still a jump.
      if (!documentId && response?.id) {
        setCreatedId(response.id);
      }
      // The save answers with the PDF as it left it: kept, filed, or why not.
      // Without this, View PDF and a filing problem only showed on reopening.
      if (response?.captured_document !== undefined) {
        setCapturedDocument(response.captured_document);
      }
      
    } catch (error) {
      console.error('Error saving draft:', error);
      setDuplicateInvoice(error?.response?.data?.duplicate_invoice || null);
      showError(extractApiError(error, 'Failed to save draft.'), 8000);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleSubmitApproval = async (modifiedData = null) => {
    if (refuseUnloadedSave()) return;
    try {
      setIsSaving(true);
      
      // Use modified data from Screen3 if provided, otherwise use state
      const dataToUse = modifiedData || invoiceData;
      
      console.log('=== WORKFLOW VALIDATION DEBUG ===');
      console.log('Full dataToUse:', dataToUse);
      console.log('hms_entity value:', dataToUse.hms_entity);
      console.log('partner value:', dataToUse.partner);
      
      // Validate ALL required fields and collect errors
      const errors = [];
      let targetScreen = null;
      
      // Screen 1 required fields - values are direct integers, not objects
      if (!dataToUse.hms_entity) {
        errors.push('Entity is required');
        targetScreen = 1;
      }
      if (!dataToUse.supplier_invoice_number || !dataToUse.supplier_invoice_number.trim()) {
        errors.push('Supplier Invoice Number is required');
        targetScreen = targetScreen || 1;
      }
      if (!dataToUse.invoice_date) {
        errors.push('Invoice Date is required');
        targetScreen = targetScreen || 1;
      }
      if (!dataToUse.partner) {
        errors.push('Supplier (CRM Profile) is required');
        targetScreen = targetScreen || 1;
      }
      if (!dataToUse.due_date) {
        errors.push('Due Date is required');
        targetScreen = targetScreen || 1;
      }
      if (!dataToUse.payment_terms) {
        errors.push('Payment Terms is required');
        targetScreen = targetScreen || 1;
      }
      
      // Screen 2 required fields - line items validation
      if (lineItems.length === 0) {
        errors.push('At least one line item is required');
        targetScreen = targetScreen || 2;
      } else {
        // Validate each line item
        lineItems.forEach((item, index) => {
          if (!item.item_description || !item.item_description.trim()) {
            errors.push(`Line ${index + 1}: Description is required`);
            targetScreen = targetScreen || 2;
          }
          if (item.quantity === null || item.quantity === undefined || item.quantity === '') {
            errors.push(`Line ${index + 1}: Quantity is required`);
            targetScreen = targetScreen || 2;
          }
          if (item.unit_price === null || item.unit_price === undefined || item.unit_price === '') {
            errors.push(`Line ${index + 1}: Unit Price is required`);
            targetScreen = targetScreen || 2;
          }
          if (!item.vat_rate && item.vat_rate !== 'CUSTOM') {
            errors.push(`Line ${index + 1}: VAT Rate is required`);
            targetScreen = targetScreen || 2;
          }
          if (item.vat_rate === 'CUSTOM' && (item.custom_vat_percentage === '' || item.custom_vat_percentage === null || item.custom_vat_percentage === undefined)) {
            errors.push(`Line ${index + 1}: Custom VAT percentage is required`);
            targetScreen = targetScreen || 2;
          }
        });
      }
      
      // Validate total amount
      const total = parseFloat(dataToUse.gross_amount || 0);
      if (total <= 0) {
        errors.push('Invoice total must be greater than zero');
        targetScreen = targetScreen || 2;
      }
      
      // Screen 3 required fields
      if (!dataToUse.reporting_month) {
        errors.push('Reporting Month is required');
        targetScreen = targetScreen || 3;
      }
      
      if (errors.length > 0) {
        setDraftValidationErrors(errors);
        setTargetScreenForValidation(targetScreen);
        setShowDraftValidationModal(true);
        setIsSaving(false);
        return;
      }
      
      const payload = {
        ...dataToUse,
        status: 'PENDING_APPROVAL',
        reporting_month: dataToUse.reporting_month 
          ? (dataToUse.reporting_month.length === 7 
              ? dataToUse.reporting_month + '-01'
              : dataToUse.reporting_month)
          : null,
        line_items: prepareLineItemsForApi(lineItems),
        vessel_info: vesselInfo
      };
      
      let response;
      if (documentId) {
        response = await invoicingService.updateIncomingInvoice(documentId, payload);
      } else {
        response = await invoicingService.createIncomingInvoice(payload);
      }
      
      showSuccess(
        `Incoming invoice ${isEditMode ? 'updated and ' : ''}submitted for approval! IMT Expense Number: ${response.hms_expense_number}, ` +
        `Supplier Invoice: ${response.supplier_invoice_number}, Status: Pending Approval, ` +
        `Amount Payable: ${response.currency} ${response.gross_amount}`,
        8000
      );
      
      // Redirect to incoming list
      setTimeout(() => goBackAfterSave(response?.id || id), 1500);
      
    } catch (error) {
      console.error('Error submitting invoice:', error);
      setDuplicateInvoice(error?.response?.data?.duplicate_invoice || null);
      showError(extractApiError(error, 'Failed to submit invoice for approval.'), 10000);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleFinancialApprovalAction = async ({ action, rejection_reason, notes }) => {
    try {
      let response;
      
      if (action === 'approve') {
        response = await invoicingService.approveIncomingInvoice(id, notes);
        const expenseNumber = response?.invoice?.hms_expense_number || invoiceData.hms_expense_number;
        showSuccess(`${expenseNumber} approved successfully!`, 8000);
      } else if (action === 'reject') {
        response = await invoicingService.rejectIncomingInvoice(id, rejection_reason);
        showSuccess(`Incoming invoice rejected. The submitter has been notified.`, 8000);
      }
      
      const updatedInvoice = await invoicingService.fetchIncomingInvoice(id);
      setInvoiceStatus(updatedInvoice.status);
      setInvoiceData(prev => ({ ...prev, hms_expense_number: updatedInvoice.hms_expense_number }));
      
      setTimeout(() => goBackAfterSave(id), 2000);
    } catch (error) {
      console.error('Error processing approval action:', error);
      const errorMessage = error.response?.data?.error || error.message;
      showError(errorMessage, 8000);
    }
  };
  
  // Shared pill stepper. `completed` keeps the old visited-before meaning:
  // any screen behind the current one is ticked. Free-jump is unchanged.
  const stepperSections = [
    { id: '1', label: 'Supplier & Header', completed: currentScreen > 1 },
    { id: '2', label: 'Products', completed: currentScreen > 2 },
    { id: '3', label: 'Bank & Notes', completed: currentScreen > 3 }
  ];
  
  if (isLoading) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="invoice-page-container incoming-invoice-screen">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading invoice...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (id && loadProblem) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="invoice-page-container incoming-invoice-screen">
          <RecordLoadProblem
            problem={loadProblem}
            recordName="supplier invoice"
            areaName="invoicing"
            backLabel={returnLabel}
            onBack={goBack}
            onRetry={() => setLoadAttempt((n) => n + 1)}
          />
        </div>
      </Layout>
    );
  }

  // Mode plus the "came from" note, in the shared header's title slot. The old
  // all-caps was the legacy header's own styling; the shared title carries no
  // transform.
  const headerTitle = [
    isEditMode ? 'Edit Incoming Invoice' : 'Create Incoming Invoice',
    sourcePO && '(from purchase order)',
  ].filter(Boolean).join(' ');

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      {/* Validation Modal */}
      <InvoiceValidationModal
        isOpen={showDraftValidationModal}
        onClose={() => {
          setShowDraftValidationModal(false);
          if (targetScreenForValidation && targetScreenForValidation !== currentScreen) {
            setCurrentScreen(targetScreenForValidation);
          }
        }}
        title="Complete Required Fields"
        errors={draftValidationErrors}
        fieldIds={[]}
      />
      
      <div className="invoice-page-container incoming-invoice-screen">
        {/* The arrow goes back to wherever this screen was opened from
            (useDocumentReturn); backLabel is its "Back to ..." wording. */}
        <HMSPageHeader
          onBack={goBack}
          backLabel={returnLabel}
          title={headerTitle}
          kicker="Finance"
          className="invoice-header--papers"
          actions={
            <>
              {/* The delivery link rides in the actions cluster as a chip; it
                  used to sit under the old header's subtitle line. */}
              <DeliveryLinkBadge
                deliveryId={invoiceData.source_delivery || invoiceData._prefilled_from_delivery}
                deliveryNumber={invoiceData.source_delivery_number || invoiceData._prefilled_delivery_number}
              />

              {id && <PinToDeskButton entityType="incoming_invoice" objectId={id} />}

              {(invoiceStatus !== 'APPROVED' || !isEditMode) && (
                <>
                  {/* THE missing button (Marcel, point 8). It sits on the screen
                      rather than on the door, so it is there whether the operator
                      arrived from a delivery, from the list, or from the menu —
                      and capturing here keeps the delivery link. */}
                  <button
                    className="btn-secondary"
                    onClick={() => setIsCaptureModalOpen(true)}
                    disabled={isSaving}
                    style={{ minWidth: '150px' }}
                    title="Read a supplier PDF into this form"
                  >
                    <FontAwesomeIcon icon={faFileUpload} /> Capture Invoice
                  </button>
                  {!documentId && (
                    <>
                      <input
                        ref={attachInputRef}
                        type="file"
                        accept={SUPPLIER_DOCUMENT_ACCEPT}
                        onChange={handleAttachFile}
                        style={{ display: 'none' }}
                        data-testid="attach-document-input"
                      />
                      <button
                        className="btn-secondary"
                        onClick={() => attachInputRef.current && attachInputRef.current.click()}
                        disabled={isSaving || isAttaching}
                        style={{ minWidth: '160px' }}
                        title="Attach the supplier's invoice without having it read"
                      >
                        <FontAwesomeIcon icon={faPaperclip} /> {isAttaching ? 'Attaching…' : 'Attach document'}
                      </button>
                    </>
                  )}
                  <button
                    className="btn-secondary"
                    onClick={() => handleSaveDraft()}
                    disabled={isSaving || needsDocumentFirst}
                    title={needsDocumentFirst ? NEEDS_DOCUMENT_MESSAGE : undefined}
                    style={{ minWidth: '120px' }}
                  >
                    <FontAwesomeIcon icon={faSave} /> Save Draft
                  </button>
                  {sendToFinance.showSendButton && (
                    <button
                      className="btn-primary"
                      onClick={() => handleSubmitApproval()}
                      disabled={isSaving || needsDocumentFirst}
                      title={needsDocumentFirst ? NEEDS_DOCUMENT_MESSAGE : undefined}
                      style={{ minWidth: '160px' }}
                    >
                      <FontAwesomeIcon icon={faPaperPlane} /> Save & Workflow
                    </button>
                  )}
                </>
              )}
              {documentId && (
                <>
                  <button
                    className="btn-secondary"
                    onClick={() => setShowSupplierPdfModal(true)}
                    style={{ minWidth: '170px' }}
                    title="Preview the supplier's invoice PDF"
                  >
                    <FontAwesomeIcon icon={faFilePdf} /> Preview Supplier PDF
                  </button>
                  {capturedDocument?.kept && (
                    <button
                      className="btn-secondary"
                      onClick={openCapturedPdf}
                      style={{ minWidth: '120px' }}
                      title={`Open ${capturedDocument.filename}, the PDF this invoice was captured from`}
                    >
                      <FontAwesomeIcon icon={faFilePdf} />{' '}
                      {/\.pdf$/i.test(capturedDocument.filename || '') ? 'View PDF' : 'View document'}
                    </button>
                  )}
                  <button
                    className="btn-secondary"
                    onClick={() => setShowHistory(true)}
                    style={{ minWidth: '110px' }}
                    title="Who created and changed this invoice, and when"
                  >
                    <FontAwesomeIcon icon={faHistory} /> History
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => setIsDocumentPanelOpen(prev => !prev)}
                    style={{ minWidth: '120px', position: 'relative' }}
                    title="View invoice documents"
                  >
                    <FontAwesomeIcon icon={faFolderOpen} /> Documents
                    {documentCount > 0 && (
                      <span style={{
                        position: 'absolute', top: '-6px', right: '-6px',
                        background: '#3b82f6', color: 'white', fontSize: '0.7rem',
                        fontWeight: 700, borderRadius: '50%', minWidth: '18px', height: '18px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '0 4px'
                      }}>
                        {documentCount}
                      </span>
                    )}
                  </button>
                  {id && (

                    <AbacusStatus model="IncomingInvoice" recordId={id} />

                  )}
                </>
              )}

              {isEditMode && (
                <button
                  className="btn-secondary"
                  onClick={invoicePapers.toggle}
                  style={{ minWidth: '140px' }}
                  title="Show the delivery papers and contracts beside this invoice"
                  aria-pressed={invoicePapers.isOpen}
                  data-testid="invoice-papers-toggle"
                >
                  <FontAwesomeIcon icon={faColumns} /> Invoice Papers
                </button>
              )}
            </>
          }
        />
        
        {/* Progress Indicator */}
        {/* [ Invoice | Invoice Papers ]: the same split as the delivery's
            Contracts & Notes. Closed, the invoice renders exactly as before. */}
        <div className={`record-split invoice-papers-split${documentId && invoicePapers.isOpen ? ' record-split--open' : ''}`}>
        <div className="record-split-content invoice-papers-record">
        <div className="hms-stepper-bar">
          <HMSStepper
            sections={stepperSections}
            activeId={String(currentScreen)}
            onStepClick={(id) => setCurrentScreen(Number(id))}
          />
        </div>
        
        {/* The PDF could not even be kept: point to Documents (no Retry, nothing
            to retry). Worded so it stays true once the PDF has been added there. */}
        {documentId && capturedDocument?.filing_problem && !capturedDocument.kept && (
          <div className="capture-filing-problem" role="alert" style={{
            margin: '0.75rem 0', padding: '0.6rem 1rem', borderRadius: '0.375rem',
            background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', fontSize: '0.875rem',
          }}>
            <FontAwesomeIcon icon={faExclamationTriangle} /> The captured PDF could not be kept with this
            invoice. If it isn't under Documents yet, add it there.
          </div>
        )}

        {/* The PDF is kept here but did not reach the archive: say so, with Retry. */}
        {documentId && capturedDocument?.filing_problem && capturedDocument.kept && (
          <div className="capture-filing-problem" role="alert" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem',
            margin: '0.75rem 0', padding: '0.6rem 1rem', borderRadius: '0.375rem',
            background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', fontSize: '0.875rem',
          }}>
            <span>
              <FontAwesomeIcon icon={faExclamationTriangle} /> The PDF is kept with this invoice but is not
              in the archive yet: {capturedDocument.filing_problem}
            </span>
            <button className="btn-secondary" onClick={retryFiling} disabled={retryingFiling}>
              <FontAwesomeIcon icon={faRedo} spin={retryingFiling} /> {retryingFiling ? 'Filing...' : 'Retry'}
            </button>
          </div>
        )}

        {/* Financial Approver Banner */}
        {isEditMode && invoiceStatus === 'PENDING_APPROVAL' && user?.is_financial_approver && (
          <div className="financial-approver-banner">
            <div className="financial-approver-banner-content">
              <div className="financial-approver-banner-icon">
                <FontAwesomeIcon icon={faUserShield} />
              </div>
              <div className="financial-approver-banner-text">
                <h4>Financial Approval Required</h4>
                <p>This incoming invoice is pending financial approval. As a Financial Approver, you can approve or reject this invoice.</p>
              </div>
              <button
                className="financial-approver-action-btn"
                onClick={() => setShowApprovalModal(true)}
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                Approve / Reject
              </button>
            </div>
          </div>
        )}
        
        {/* This supplier invoice is already in HMS. Blocking, and it stays put:
            the user's next move is to open the invoice that already holds the
            number, not to retype it. */}
        {duplicateInvoice && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #dc2626',
            borderLeft: '4px solid #dc2626',
            borderRadius: '8px',
            padding: '0.75rem 1rem',
            margin: '0 0 1rem 0',
            color: '#991b1b',
            fontSize: '0.875rem',
          }}>
            <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              This supplier invoice is already booked in IMT.
            </div>
            <div style={{ margin: '0.5rem 0 0 0' }}>
              {duplicateInvoice.hms_expense_number
                ? `${duplicateInvoice.hms_expense_number} already carries this number`
                : 'An existing invoice already carries this number'}
              {duplicateInvoice.status ? ` (status: ${duplicateInvoice.status.replace(/_/g, ' ').toLowerCase()})` : ''}.
              {' '}
              <button
                type="button"
                onClick={() => history.push(`/invoicing/incoming/${duplicateInvoice.id}`)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: '#991b1b',
                  fontWeight: 700,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                Open it
              </button>
              {' '}to check it, add documents, or move it through approval.
            </div>
          </div>
        )}

        {/* Why Save & Workflow is absent on a bunker invoice (2026-09-17) --
            with the screen's other banners, in their shape. */}
        {(invoiceStatus !== 'APPROVED' || !isEditMode) && (
          <SendToFinanceNote sendToFinance={sendToFinance} />
        )}

        {!documentId && invoiceData._capture_analysis_id && attachedFileName && (
          <div className="send-to-finance-note" role="status">
            <FontAwesomeIcon icon={faPaperclip} className="send-to-finance-note-icon" />
            <div>
              Supplier's document attached: <strong>{attachedFileName}</strong>. It will be filed with the invoice when you save.
            </div>
          </div>
        )}

        {needsDocumentFirst && (
          <div className="send-to-finance-note send-to-finance-note--warning" role="status">
            <FontAwesomeIcon icon={faFileUpload} className="send-to-finance-note-icon" />
            <div>{NEEDS_DOCUMENT_MESSAGE}</div>
          </div>
        )}

        {/* CRM cross-check flags (advisory — never blocks save/approval) */}
        {crmMatch && (
          <CrmMatchFlags checks={crmMatch.checks} overall={crmMatch.overall} />
        )}

        {/* Screen Content */}
        <div className="invoice-screens-container">
          {currentScreen === 1 && (
            <IncomingInvoiceScreen1
              invoiceData={invoiceData}
              setInvoiceData={setInvoiceData}
              onNext={() => setCurrentScreen(2)}
              sourcePO={sourcePO}
              captureSupplierName={invoiceData._capture_supplier_name || null}
              capturePartner={capturePartner}
              issuerName={documentIssuerName}
              isPersisted={Boolean(id)}
            />
          )}
          
          {currentScreen === 2 && (
            <IncomingInvoiceScreen2
              invoiceData={invoiceData}
              setInvoiceData={setInvoiceData}
              lineItems={lineItems}
              setLineItems={setLineItems}
              vesselInfo={vesselInfo}
              setVesselInfo={setVesselInfo}
              onPrevious={() => setCurrentScreen(1)}
              onNext={() => setCurrentScreen(3)}
            />
          )}
          
          {currentScreen === 3 && (
            <IncomingInvoiceScreen3
              invoiceData={invoiceData}
              setInvoiceData={setInvoiceData}
              lineItems={lineItems}
              vesselInfo={vesselInfo}
              onPrevious={() => setCurrentScreen(2)}
              isReadOnly={isReadOnly}
            />
          )}
        </div>
        </div>

        {documentId && (
          <DocumentsSplitPane
            isOpen={invoicePapers.isOpen}
            onClose={invoicePapers.close}
            title="Invoice Papers"
          >
            <RelatedDocumentsPane kind="incoming" invoiceId={documentId} />
          </DocumentsSplitPane>
        )}
        </div>
        
        {/* Footer */}
        <Footer />
      </div>
      
      {/* Financial Approval Modal */}
      {showApprovalModal && (
        <FinancialApprovalModal
          isOpen={showApprovalModal}
          onClose={() => setShowApprovalModal(false)}
          invoice={{
            ...invoiceData,
            status: invoiceStatus,
            id: id
          }}
          isFinancialApprover={user?.is_financial_approver}
          onApprovalAction={handleFinancialApprovalAction}
          warnings={bdnApprovalWarnings(lineItems)}
        />
      )}

      {/* Invoice Document Panel */}
      {/* `documentId`, NOT `id`. Save Draft deliberately keeps you on this screen
          and adopts the new record into STATE rather than the address bar (see
          handleSaveDraft), so after a first save the URL is still /create and
          `id` is still undefined. Handing that to the panel meant an uploaded
          file was attached to nothing — the file simply vanished. The fix is the
          right id, not a looser permission: the server already accepts an upload
          for an invoice it has just created. */}
      <InvoiceDocumentPanel
        isOpen={isDocumentPanelOpen}
        onClose={() => setIsDocumentPanelOpen(false)}
        invoiceId={documentId}
        showSuccess={showSuccess}
        showError={showError}
        onDocumentCountChange={setDocumentCount}
      />

      <InvoiceAuditModal
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        invoiceType="incoming"
        invoiceId={documentId || null}
        invoiceNumber={null}
      />

      {/* Supplier PDF Preview Modal */}
      <SupplierPdfChooserModal
        isOpen={showSupplierPdfModal}
        invoiceId={documentId}
        onClose={() => setShowSupplierPdfModal(false)}
        showError={showError}
      />

      {/* Capture — the same modal the Incoming Invoices list mounts, but here it
          fills the form IN PLACE instead of navigating, so nothing already on
          the screen (above all the delivery link) is thrown away. */}
      <InvoiceCaptureModal
        isOpen={isCaptureModalOpen}
        onClose={() => setIsCaptureModalOpen(false)}
        onSuccess={({ selectedData, analysisId, crmPartner, attachedOnly }) => {
          setIsCaptureModalOpen(false);
          applyCapturedInvoice(selectedData, analysisId, crmPartner || null, Boolean(attachedOnly));
        }}
      />
    </Layout>
  );
};

export default CreateIncomingInvoice;

